// utils/API/BackendHelper/certificate.js
import ApiHelper from '../ApiHelper';
import API_ENDPOINTS from '../endpoints';

/**
 * Certificate API Helper
 * Handles all certificate-related API calls
 */
class CertificateHelper {
  /**
   * Get public certificate details (no authentication required)
   * @param {string} certificateId - Certificate ID
   * @returns {Promise<Object>} Certificate details with student, course, and college info
   */
  static async getCertificateDetailsPublic(certificateId) {
    const endpoint = API_ENDPOINTS.PUBLIC.CERTIFICATE_DETAILS(certificateId);
    return await ApiHelper.get(endpoint);
  }

  /**
   * Get student certificate details (requires student authentication)
   * @param {string} certificateId - Certificate ID
   * @returns {Promise<Object>} Certificate details
   */
  static async getCertificateDetails(certificateId) {
    const endpoint = API_ENDPOINTS.STUDENTS.CERTIFICATES.DETAILS(certificateId);
    return await ApiHelper.get(endpoint);
  }

  /**
   * Check if certificate is available for a course
   * @param {string} courseId - Course ID
   * @returns {Promise<Object>} Certificate availability status
   */
  static async checkCertificateAvailability(courseId) {
    const endpoint = API_ENDPOINTS.STUDENTS.CERTIFICATES.CHECK_AVAILABILITY(courseId);
    return await ApiHelper.get(endpoint);
  }

  /**
   * Get certificate by course ID
   * @param {string} courseId - Course ID
   * @returns {Promise<Object>} Certificate data
   */
  static async getCertificateByCourse(courseId) {
    const endpoint = API_ENDPOINTS.STUDENTS.CERTIFICATES.BY_COURSE(courseId);
    return await ApiHelper.get(endpoint);
  }

  /**
   * Download certificate PDF
   * @param {string} certificateId - Certificate ID
   * @returns {Promise<Blob>} Certificate PDF blob
   */
  static async downloadCertificate(certificateId) {
    const endpoint = API_ENDPOINTS.STUDENTS.CERTIFICATES.DOWNLOAD(certificateId);
    return await ApiHelper.get(endpoint, { responseType: 'blob' });
  }

  /**
   * Transform API response to certificate generator format
   * @param {Object} apiResponse - API response from getCertificateDetails
   * @returns {Object} Certificate data in generator format
   */
  static transformToCertificateData(apiResponse) {
    const { certificate, student, course, college, platform, principal } = apiResponse.data;

    return {
      // Certificate Info
      certificate_id: certificate.id,
      certificate_number: certificate.certificate_number,
      issue_date: certificate.issued_at,
      issued_date: certificate.issued_at,
      status: certificate.status,
      grade: certificate.grade,

      // Student Info
      student_id: student.id,
      student_name: student.student_name,
      parent_name: student.father_name || student.mother_name,
      father_name: student.father_name,
      mother_name: student.mother_name,
      student_email: student.email,
      student_mobile: student.mobile,

      // Course Info
      course_id: course.id,
      course_name: course.course_name,
      credits: course.credits?.toString() || '0',
      
      // Use issued_at as assessment_date if no specific date provided
      assessment_date: certificate.issued_at,

      // College Info
      college_id: college.id,
      college_name: college.college_name,
      college_code: college.college_code,
      college_address: college.address,
      college_email: college.contact_email,
      college_phone: college.contact_phone,
      college_logo_url: college.logo_url,

      // Principal Info (from college's principal data or platform)
      principal_name: principal?.principal_name || college.principal_name || 'Principal',
      principal_signature_url: principal?.signature_url || college.principal_signature_url || platform?.principal_signature_url,
      
      // Platform Info
      director_name: 'Ramit Raman Rastogi',
      director_signature_url: platform?.director_signature_url || null,
      platform_logo_url: platform?.logo_url,
      gyansathi_logo_url: platform?.logo_url,
      university_logo_url: platform?.university_logo_url || null,

      // Verification
      verification_url: `${window.location.origin}/verify/${certificate.certificate_number}`,
    };
  }

  /**
   * Fetch and transform certificate data for PDF generation
   * @param {string} certificateId - Certificate ID
   * @param {boolean} usePublicEndpoint - Whether to use public endpoint (default: false)
   * @returns {Promise<Object>} Transformed certificate data ready for PDF generation
   */
  static async fetchCertificateForPDF(certificateId, usePublicEndpoint = false) {
    let response;
    
    if (usePublicEndpoint) {
      response = await this.getCertificateDetailsPublic(certificateId);
    } else {
      response = await this.getCertificateDetails(certificateId);
    }

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch certificate details');
    }

    return this.transformToCertificateData(response);
  }
}

export default CertificateHelper;
